import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const IDLE_MS = 15 * 60 * 1000; // 15 minutes of inactivity → auto sign-out

/**
 * HIPAA automatic-logoff (§164.312(a)(2)(iii)) for shared facility tablets.
 * When `enabled`, signs the inmate out after IDLE_MS of no interaction so the
 * next person can't see the prior inmate's PHI. Resets on any user activity.
 */
export function useIdleLogout(enabled) {
    const { logout } = useAuth();
    const timer = useRef(null);

    useEffect(() => {
        if (!enabled) return undefined;

        const signOut = async () => {
            try { await logout(); } catch (e) { /* noop */ }
            toast("Signed out due to inactivity to protect your privacy.");
            window.location.href = "/";
        };

        const reset = () => {
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(signOut, IDLE_MS);
        };

        const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
        events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
        reset();

        return () => {
            if (timer.current) clearTimeout(timer.current);
            events.forEach((e) => window.removeEventListener(e, reset));
        };
    }, [enabled, logout]);
}
